import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { apiFetch } from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import {
  PassportEventListDto,
  type PassportEventListDto as PassportEventListDtoType,
} from '@tunodo/ticketing-shared';
import { ChevronRight, Plus } from 'lucide-react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_app/admin/passport/')({
  beforeLoad: () => {
    const { user } = useAuthStore.getState();
    if (user?.role !== 'ORGANIZER') {
      toast.info('Solo los organizadores pueden acceder al panel de pasaporte.');
      throw redirect({ to: '/' });
    }
  },
  component: PassportAdminIndexPage,
});

function PassportAdminIndexPage() {
  const { data, isLoading, isError } = useQuery<PassportEventListDtoType>({
    queryKey: ['passport-admin', 'events'],
    queryFn: () =>
      apiFetch('/api/passport-admin/events', {
        schema: PassportEventListDto,
      }),
  });

  return (
    <main className="flex-1 p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Pasaporte — Eventos</h1>
          <div className="text-muted-foreground mt-1 text-sm">
            {isLoading ? <Skeleton className="h-4 w-28" /> : <>{data?.length ?? 0} eventos</>}
          </div>
        </div>
        <Button asChild className="gap-2">
          <Link to="/admin/passport/new">
            <Plus className="h-4 w-4" />
            Nuevo evento
          </Link>
        </Button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="rounded-md border overflow-x-auto">
          <Table className="min-w-[500px]">
            <TableHeader>
              <TableRow>
                <TableHead>Título</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Secciones</TableHead>
                <TableHead>Actividades</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 4 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton className="h-4 w-48" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-12" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-12" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-6" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Error */}
      {isError && !isLoading && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-6 text-center">
          <p className="text-sm text-destructive font-medium">No se pudieron cargar los eventos.</p>
        </div>
      )}

      {/* Data */}
      {!isLoading && !isError && (
        <div className="rounded-md border overflow-x-auto">
          <Table className="min-w-[500px]">
            <TableHeader>
              <TableRow>
                <TableHead>Título</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Secciones</TableHead>
                <TableHead>Actividades</TableHead>
                <TableHead className="w-24">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!data?.length ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                    No hay eventos. Crea uno con "+ Nuevo evento".
                  </TableCell>
                </TableRow>
              ) : (
                data.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell className="font-medium">{event.title}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {event.date
                        ? new Intl.DateTimeFormat('es-CO', { dateStyle: 'medium' }).format(
                            new Date(`${event.date.slice(0, 10)}T12:00:00`),
                          )
                        : '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{event.sectionCount}</TableCell>
                    <TableCell className="text-muted-foreground">{event.activityCount}</TableCell>
                    <TableCell>
                      <Button asChild variant="ghost" size="sm" className="gap-1">
                        <Link to="/admin/passport/$eventId" params={{ eventId: event.id }}>
                          Ver detalle
                          <ChevronRight className="h-4 w-4" />
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </main>
  );
}
